import React, { useEffect, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { selectCurrentStepId } from 'redux/placement';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Tooltip as CTooltip } from 'lib/components/Tooltip';
import { ColumnButton } from 'lib/components/ColumnButton';
import classNames from 'classnames';
import { AnyAction } from '@reduxjs/toolkit';
import { CONFIRM_PROOF } from './helpers/calculatePlacementSteps';

export type ScrollStepBaseProps = {
  id: string;
  formId?: string;
  complete?: boolean;
  next?: () => Promise<void | AnyAction> | void;
  nextText?: string;
  nextTooltipText?: string;
  previous?: () => void;
  secondaryAction?: {
    buttonText: string;
    buttonId: string;
    onClick: () => void;
    tooltipText?: string;
  };
  canSkip?: boolean;
  skip?: null | (() => void | null | Promise<void | AnyAction>);
  skipText?: string;
  skipTooltipText?: string;
  onDisabledStepClick?: (id: string) => void;
  children: any;
  overrideStyles?: boolean;
  caption?: React.ReactNode;
  title?: string;
  onHoldText?: string;
};

function ScrollStepContainer({
  id,
  formId,
  complete,
  next,
  nextText,
  nextTooltipText,
  previous,
  secondaryAction,
  canSkip,
  skip,
  skipText,
  skipTooltipText,
  onDisabledStepClick,
  children,
  overrideStyles,
  title,
  caption,
  onHoldText
}: ScrollStepBaseProps) {
  const currentStepId = useAppSelector(selectCurrentStepId);
  const [nextLoading, setNextLoading] = useState(false);

  // Hide the step body until the step is active, unless it's the CONFIRM_PROOF step
  const [hideStepBody, setHideStepBody] = useState(id !== CONFIRM_PROOF);

  const isActive = id === currentStepId;

  // Unfurl step content when the step becomes active in placement
  useEffect(() => {
    if (isActive) {
      setHideStepBody(false);
    }
  }, [isActive]);

  return (
    <section
      title={title}
      className="border-column-gray-100 border rounded-md w-full shadow-column-3"
      onClick={() => {
        onDisabledStepClick && onDisabledStepClick(id);
      }}
      id={id}
    >
      <div
        className={classNames('w-full', {
          'pointer-events-none opacity-30': !isActive
        })}
      >
        <div className="content-center bg-white rounded-md">
          {(title || caption) && (
            <div
              className={classNames(
                'flex flex-row w-full place-content-between p-8',
                {
                  'border-b border-column-gray-100': !hideStepBody
                }
              )}
            >
              <div className="relative flex-col space-y-2">
                {title && (
                  <h2 className="text-lg text-column-gray-700 font-semibold">
                    {title}
                  </h2>
                )}
                {caption && (
                  <p className="text-column-gray-500 font-medium text-sm">
                    {caption}
                  </p>
                )}
              </div>
            </div>
          )}
          {!hideStepBody && (
            <>
              <div
                className={classNames({
                  'p-8': !overrideStyles
                })}
              >
                {children}
              </div>
              {(previous || next) && (
                <div className="text-right w-full rounded-b-md border-t border-column-gray-100 py-6 px-8 bg-base-2 rounded-md">
                  <div
                    className={classNames('flex flex-row', {
                      'justify-between': previous || secondaryAction,
                      'justify-end': !previous && !secondaryAction
                    })}
                  >
                    <div className="flex flex-row justify-start">
                      {previous && (
                        <div className="bg-transparent text-right">
                          <span className="inline-flex rounded-md">
                            <ColumnButton
                              tertiary
                              size="lg"
                              onClick={() => {
                                void previous();
                              }}
                              buttonText="Previous"
                              type="button"
                            />
                          </span>
                        </div>
                      )}
                      {secondaryAction && (
                        <div className={classNames({ 'ml-2': previous })}>
                          <CTooltip
                            helpText={secondaryAction.tooltipText || ''}
                            customPosition={`text-left ${
                              !previous ? 'left-0' : 'right-0'
                            }`}
                          >
                            <ColumnButton
                              tertiary
                              id={secondaryAction.buttonId}
                              size="lg"
                              onClick={secondaryAction.onClick}
                              buttonText={secondaryAction.buttonText}
                              type="button"
                            />
                          </CTooltip>
                        </div>
                      )}
                    </div>
                    {(next || formId || skip || onHoldText) && (
                      <div className="flex flex-row justify-end">
                        {skip && (
                          <div>
                            <CTooltip
                              helpText={skipTooltipText || ''}
                              position="right"
                              classes="text-left"
                            >
                              <div className="bg-transparent text-right">
                                <span className="inline-flex rounded-md shadow-sm">
                                  <ColumnButton
                                    secondary
                                    size="lg"
                                    id={`${id}-skip-button`}
                                    onClick={() => {
                                      void skip();
                                    }}
                                    disabled={!canSkip}
                                    buttonText={skipText || 'Skip'}
                                    type="button"
                                  />
                                </span>
                              </div>
                            </CTooltip>
                          </div>
                        )}
                        {onHoldText && (
                          <CTooltip
                            classes={'text-left max-w-4'}
                            customPosition="top-0 -mt-16 -ml-16"
                            helpText={onHoldText}
                          >
                            <ExclamationCircleIcon className="w-4 mt-3 -mr-2 text-red-550" />
                          </CTooltip>
                        )}
                        {(next || formId) && (
                          <div className="ml-2">
                            <CTooltip
                              helpText={nextTooltipText || ''}
                              customPosition="-ml-24 text-left"
                            >
                              <div className="bg-transparent text-right">
                                <span className="inline-flex rounded-md shadow-sm">
                                  <ColumnButton
                                    primary
                                    size="lg"
                                    id={`${id}-button`}
                                    formId={formId}
                                    onClick={
                                      next
                                        ? async () => {
                                            setNextLoading(true);
                                            await next();
                                            setNextLoading(false);
                                          }
                                        : undefined
                                    }
                                    buttonText={nextText || 'Next'}
                                    loading={nextLoading}
                                    disabled={!complete}
                                    type={formId ? 'submit' : 'button'}
                                  />
                                </span>
                              </div>
                            </CTooltip>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default ScrollStepContainer;
